import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  Auth,
  authState,
  signOut,
  signInWithCustomToken,
  signInWithPopup,
  OAuthProvider,
  setPersistence,
  indexedDBLocalPersistence,
  User as FirebaseUser
} from '@angular/fire/auth';
import { FirebaseError } from '@angular/fire/app';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, of, BehaviorSubject, EMPTY, firstValueFrom } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { User } from '../_models/user';
import { consumePreviewReturnUrl } from './preview-return-url';

const microsoftAuthProvider = new OAuthProvider('microsoft.com');
microsoftAuthProvider.setCustomParameters({ prompt: 'select_account' });

const CB_EPL_LOGOUT_URL =
  'https://cbaccount.collegeboard.org/professional/enroll?prgCode=NPCAdmin&appId=488';

function firebaseErrorMessage(err: unknown, fallback: string): string {
  return err instanceof FirebaseError ? err.message : fallback;
}

function hasAuthProvider(firebaseUser: FirebaseUser, providerId: string): boolean {
  return firebaseUser.providerData.some(provider => provider.providerId === providerId);
}

@Injectable()
export class AuthService {

  user: Observable<User | null>;
  noUser = new BehaviorSubject(false);
  eplLoginError$ = new BehaviorSubject<string | null>(null);
  eplLoginInProgress$ = new BehaviorSubject(false);

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private functions: Functions,
    private router: Router
  ) {
    this.user = authState(this.auth).pipe(
      switchMap(firebaseUser => {
        if (!firebaseUser) {
          this.noUser.next(true);
          return of(null);
        }
        if (firebaseUser.isAnonymous) {
          this.logout();
          return EMPTY;
        }
        this.noUser.next(false);
        const userDocRef = doc(this.firestore, `admin_users/${firebaseUser.uid}`);
        return docData(userDocRef) as Observable<User>;
      })
    );
  }

  currentUser(): Promise<User | null> {
    return firstValueFrom(this.user);
  }

  isSupportUser(user: User | null): boolean {
    return !!user && (user.admin || !!user.dev);
  }

  /** True for a Rewiretech Microsoft session. Preview sends that tab to /support if it still needs a login. */
  usesMicrosoftLogin(): boolean {
    const firebaseUser = this.auth.currentUser;
    return !!firebaseUser && hasAuthProvider(firebaseUser, 'microsoft.com');
  }

  /**
   * Move a Rewiretech session into IndexedDB before opening Preview.
   * EPL already stores its session there at sign-in.
   */
  async prepareMicrosoftPreviewTab(): Promise<boolean> {
    if (!this.usesMicrosoftLogin()) {
      return false;
    }
    try {
      await setPersistence(this.auth, indexedDBLocalPersistence);
    } catch (error) {
      console.error('Could not share the support session with Preview.', error);
    }
    return true;
  }

  async isEplAuthUser(): Promise<boolean> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser || firebaseUser.isAnonymous) {
      return false;
    }
    if (hasAuthProvider(firebaseUser, 'microsoft.com')) {
      return false;
    }
    const { signInProvider } = await firebaseUser.getIdTokenResult();
    return signInProvider === 'custom';
  }

  resolveEntryUrl(user: User | null): UrlTree {
    return user ? this.getHomeUrlTree(user) : this.router.createUrlTree(['/epl-login']);
  }

  /**
   * After Admin login, return to a validated preview URL when one was stored
   * for this tab. Otherwise continue to the Admin home route.
   */
  async goAfterLogin(user: User): Promise<void> {
    const previewReturn = consumePreviewReturnUrl();
    if (previewReturn) {
      window.location.assign(previewReturn);
      return;
    }
    await this.router.navigateByUrl(this.getHomeUrlTree(user));
  }

  getHomeUrlTree(user: User): UrlTree {
    if (user.admin) {
      return this.router.createUrlTree(['/schools']);
    }
    if (!user.schools?.length) {
      return this.router.createUrlTree(['/no-access']);
    }
    if (user.schools.length === 1) {
      return this.router.createUrlTree([`/${user.schools[0]}/dashboard`]);
    }
    return this.router.createUrlTree(['/schools']);
  }

  async supportLoginWithMicrosoft(): Promise<string | null> {
    try {
      await setPersistence(this.auth, indexedDBLocalPersistence);
      await signInWithPopup(this.auth, microsoftAuthProvider);

      if (!this.auth.currentUser?.email?.trim()?.toLowerCase()?.endsWith('@rewiretech.com')) {
        return this.endSupportSession('Use your @rewiretech.com Microsoft 365 account to sign in.');
      }

      const profile = await firstValueFrom(
        this.user.pipe(filter(() => this.auth.currentUser !== null), take(1))
      );
      if (!this.isSupportUser(profile)) {
        return this.endSupportSession(
          'This login is for Rewiretech support staff only. Your account may need to be set up in admin_users.'
        );
      }

      if (profile) {
        await this.goAfterLogin(profile);
      }
      return null;
    } catch (err) {
      if (err instanceof FirebaseError && err.code === 'auth/popup-closed-by-user') {
        return null;
      }
      return firebaseErrorMessage(err, 'Unable to sign in with Microsoft.');
    }
  }

  async requestNpcAdminAccess(): Promise<string | null> {
    try {
      const callable = httpsCallable<void, { sent: boolean }>(this.functions, 'requestNpcAdminAccess');
      await callable();
      return null;
    } catch (err) {
      return firebaseErrorMessage(err, 'Unable to send access request.');
    }
  }

  private async endSupportSession(message: string): Promise<string> {
    await this.logout(false);
    return message;
  }

  async logout(reroute = true): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    const isEpl = await this.isEplAuthUser();
    const isSupport = !!firebaseUser && hasAuthProvider(firebaseUser, 'microsoft.com');

    this.noUser.next(true);
    await signOut(this.auth);
    if (reroute) {
      if (isEpl) {
        window.location.href = CB_EPL_LOGOUT_URL;
      } else if (isSupport) {
        await this.router.navigate(['/support']);
      } else {
        await this.router.navigate(['/epl-login']);
      }
    }
  }

  /**
   * Exchanges a College Board authorization token for a Firebase session.
   */
  async eplLogin(authorizationToken: string) {
    const token = authorizationToken?.trim();
    if (!token) {
      this.eplLoginError$.next('No College Board sign-in token was found.');
      return;
    }

    const callable = httpsCallable<{ authorizationToken: string }, { customToken: string; schoolId: string; }>(
      this.functions,
      'adminCustomToken'
    );

    this.eplLoginInProgress$.next(true);
    try {
      const tokenData = await callable({ authorizationToken: token });
      const { customToken } = tokenData.data;

      await setPersistence(this.auth, indexedDBLocalPersistence);
      await signInWithCustomToken(this.auth, customToken);

      const profile = await firstValueFrom(
        this.user.pipe(filter((user): user is User => user !== null), take(1))
      );
      await this.goAfterLogin(profile);
    } catch (error) {
      this.eplLoginError$.next(firebaseErrorMessage(error, 'An unknown error occurred.'));
    } finally {
      this.eplLoginInProgress$.next(false);
    }
  }

}
